// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page, { PageProps as containerProps } from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { notificationHeaderState, xHeaderActions, xHeaderState } from "@rugby/App/Store/Reducers";
import { constants } from "@rugby/XDL/constants";
import { screen } from "@rugby/XDL/Helpers";
import { extendedMaxWidths } from "@rugby/XDL/PreStyle/breakpoints";
import { color, MediaQuery } from "@rugby/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    header: InjectableCss;
    placeholder: InjectableCss;
    pageLayout: containerProps;
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    mainNavigationWrapper: InjectableCss;
    signInWrapper: InjectableCss;
    cartWrapper: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    header: {
        css: css`
            height: ${constants.header.desktop.height}px;
            position: absolute;
            top: ${({ notificationHeight, isCmsEditingMode }: any) => {
                const secondaryNavigationHeight = isCmsEditingMode
                    ? constants.header.secondaryNavigation.heightInCMSMode
                    : constants.header.secondaryNavigation.height;
                return secondaryNavigationHeight + notificationHeight;
            }}px;
            background: white;
            z-index: 10;
            transition: all 400ms ease-out;
            width: 100%;

            @keyframes topAnimation {
                0% {
                    top: -100px;
                }

                100% {
                    top: 0px;
                }
            }
            ${screen("lte", "md")`
                position: fixed;
                top:0;
                height: ${constants.header.tablet.height}px;
               
            `}
            ${screen("lte", "sm")`
                height: ${constants.header.mobile.height}px;
            `}

            ${({ sticky }: any) =>
                sticky &&
                `   position: fixed;
                    animation-name: topAnimation;
                    animation-duration: 400ms;
                    top:0;
                    height: ${constants.header.sticky.height}px;
                `}
        `,
    },
    placeholder: {
        css: css`
            height: ${constants.header.desktop.height}px;
            ${screen("lte", "md")`
                height: ${constants.header.tablet.height}px;
            `}
            ${screen("lte", "sm")`
                height: ${constants.header.mobile.height}px;
            `}
        `,
    },
    pageLayout: {
        as: "div",
        fullWidth: [true, true, true, true, true],
        background: "transparent",
        css: css`
            padding: 15px 30px;
            box-shadow: 0 4px 12px -6px ${color("common.black.main", 0.2)};

            &&& {
                background: ${getColor("common.white")};
                overflow: visible;
                ${screen("lte", "sm")`
                    padding: 0;
                `};
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin: auto;
            max-width: ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0;
                        `,
                        css`
                            padding: 0;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[2]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[3]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[4]}px;
                            margin: 0 auto;
                        `,
                    ],
                    "min",
                )};

            .menu-icon-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            ${screen("lte", "sm")`
            flex-wrap: wrap;
            .menu-icon-text {
                display:none;
            }
        `};
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            @media (max-width: ${constants.header.breakPoints.md}px) {
                margin: 0 15px;
            }

            ${({ sticky }: any) => sticky && `padding: 5px 0 0 0;`}
            ${screen("lte", "sm")`
                max-width: 40%;
            `}
            ${screen("lte", "md")`flex-grow: 1;`}

            img {
                max-height: 85px;
                transition: all 400ms ease-out;
                ${screen("lte", "md")`
                    height: 42px;
                    width: auto;
                    margin: 0;
                `}
                ${({ sticky }: any) => sticky && `max-height: 44px;`}
            }

            ${screen("lte", "sm")`
                 flex: 0 1 100px;
                 padding: 0;
                 height: 75px;
                 align-items: center;
                 display: flex;
                 margin-right: auto;
                 margin-left: 10px;
            `}
        `,
    },
    mainNavigationWrapper: {
        css: css`
            transition: all 400ms ease-out;
            flex: 1;
            ${({ sticky }: any) => sticky && `padding-top: 5px;`}

            button.menu-toggle {
                display: none;
            }

            ${MediaQuery.lte(constants.header.breakPoints.md)} {
                order: -1;
                max-width: 20%;
                padding-top: 0;
                flex: 0;
                button.menu-toggle {
                    display: block;
                    width: auto;
                    height: auto;
                    border-radius: 0;
                    padding: 0;
                    background-color: transparent;
                    border: transparent;
                    &:focus {
                        box-shadow: none;
                    }
                    & > span {
                        color: ${getColor("primary")};
                        top: 0;
                    }
                }
            }

            ${MediaQuery.lte("md")} {
                max-width: 75px;
                button.menu-toggle {
                    margin-right: 15px;
                    top: 0;
                    width: 75px;
                    height: 75px;
                    background: ${getColor("primary")};
                    justify-content: center;
                    & > span {
                        color: ${color("common.white")};
                    }
                }
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            transition: all 400ms ease-out;
            text-align: right;
            padding-right: 30px;
            @media (max-width: ${constants.header.breakPoints.md}px) {
                flex: 1 1 auto;
            }
            ${screen("lte", "md")`flex-grow: 8;`}
            ${({ sticky }: any) => sticky && `padding: 10px 45px 0;`}
            ${({ theme }) => {
                return screen("lte", "sm")`
                    order: 1;
                    padding: 15px 20px;
                    flex-basis: 100%;
                    background-color: ${resolveColor("common.ui3", theme)};
                    color: ${resolveColor("primary", theme)};
                    && div {
                      width: 100%;
                      input {
                        height: 50px;
                      }
                      button {
                        top: 5px;
                      }
                   }
                   `;
            }}
        `,
    },
    signInWrapper: {
        css: css`
            transition: all 400ms ease-out;
            padding-right: 40px;
            ${screen("lte", "md")`max-width: 20%;`}
            ${({ sticky }: any) => sticky && `padding-top: 15px;`}
            ${screen("lte", "sm")`
                 flex:0;
                 padding:0;
                 height:75px;
                 align-items: center;
                 display: flex;
                 margin-left: auto;
                 margin-right: 10px;
            `}
        `,
    },
    cartWrapper: {
        css: css`
            transition: all 400ms ease-out;
            ${screen("lte", "md")`max-width: 20%;`}
            ${({ sticky }: any) => sticky && `padding-top: 15px;`}
            ${screen("lte", "sm")`
                 flex:0;
                 padding:0;
                 height:75px;
                 align-items: center;
                 display: flex;
                 margin-right: 10px;
            `}
        `,
    },
};

const mapStateToProps = (state: xHeaderState & notificationHeaderState) => ({
    isSticky: state?.xHeader?.isSticky,
    isNotificationHeaderHidden: state?.notificationHeader?.isNotificationHeaderHidden,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const isMobile = () => window.matchMedia("(max-width: 768px) and (hover: none)").matches;

const LogoLinksSearch: FC<Props> = ({ id, dispatch, isSticky, isNotificationHeaderHidden }) => {
    const isCmsEditingMode = getCookie(contentModeCookieName) === "Editing";
    const notificationHeight = isNotificationHeaderHidden ? 0 : constants.header.notification.height;

    const handleSticky = () => {
        const offsetY = constants.header.secondaryNavigation.height + notificationHeight + 120;

        dispatch({
            type: "Header/isSticky",
            isSticky: window.scrollY > offsetY && !isMobile(),
        });
    };

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();

        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    });

    return (
        <>
            <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.placeholder}></StyledWrapper>
            <StyledWrapper
                as="div"
                {...{
                    sticky: isSticky,
                    isCmsEditingMode,
                    notificationHeight,
                    className: `${isSticky && "sticky-header"}`,
                }}
                {...logoLinksSearchStyles.header}
            >
                <Page {...logoLinksSearchStyles.pageLayout}>
                    <StyledWrapper {...logoLinksSearchStyles.container}>
                        <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.logoWrapper}>
                            <Zone contentId={id} zoneName="Logo" />
                        </StyledWrapper>
                        <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.mainNavigationWrapper}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </StyledWrapper>
                        <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.searchMenuWrapper}>
                            <Zone contentId={id} zoneName="Search" />
                        </StyledWrapper>
                        <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.signInWrapper}>
                            <Zone contentId={id} zoneName="SignIn" />
                        </StyledWrapper>
                        <StyledWrapper {...{ sticky: isSticky }} {...logoLinksSearchStyles.cartWrapper}>
                            <Zone contentId={id} zoneName="Cart" />
                        </StyledWrapper>
                    </StyledWrapper>
                </Page>
            </StyledWrapper>
        </>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
